import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	StraightenerProduct,
	StraightenerRecipeValueRow,
	StraightenerRecipeVersion,
} from "../models/StraightenerRecipe";

export const getStraightenerProducts = async (): Promise<
	StraightenerProduct[]
> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[GetProductSizes]", [
		{ name: "OnlyActives", value: true },
	]);
	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as Array<{
			Id: number;
			Name: string;
			ProductTypeName: string;
			Dim1: number | null;
			Dim2: number | null;
			Dim3: number | null;
		}>;
		return rows.map((r) => ({
			Id: r.Id,
			Name: r.Name,
			ProductTypeName: r.ProductTypeName,
			Dim1: r.Dim1 ?? null,
			Dim2: r.Dim2 ?? null,
			Dim3: r.Dim3 ?? null,
		}));
	}
	throw new Error(resp.message || "Error fetching products");
};

export const getStraightenerRecipes = async (
	productSizeId?: number | null,
): Promise<StraightenerRecipeVersion[]> => {
	const api = new MESApiService();
	const params: spParameter[] = [
		{ name: "ProductSizeId", value: productSizeId ?? null },
	];
	const resp = await api.callV2("[MILL].[GetStraightenerRecipes]", params);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as StraightenerRecipeVersion[];
	}
	throw new Error(resp.message || "Error fetching straightener recipes");
};

export const getCurrentCrew = async (): Promise<string | null> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[GetCurrentCrew]", []);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Crew", null) as string | null;
	}
	throw new Error(resp.message || "Error fetching current crew");
};

export const getStraightenerRecipeValues = async (
	recipeId: number,
): Promise<StraightenerRecipeValueRow[]> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[GetStraightenerRecipeValues]", [
		{ name: "RecipeId", value: recipeId },
	]);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as StraightenerRecipeValueRow[];
	}
	throw new Error(resp.message || "Error fetching recipe values");
};

const escapeXml = (s: string): string =>
	s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

const buildValuesXml = (values: StraightenerRecipeValueRow[]): string => {
	const rows = values
		.map((v) => {
			const attrs = [
				`code="${v.ParameterCode}"`,
				`roll="${v.RollNumber}"`,
				v.Value != null ? `value="${v.Value}"` : "",
				v.MinValue != null ? `min="${v.MinValue}"` : "",
				v.MaxValue != null ? `max="${v.MaxValue}"` : "",
				v.TextValue != null ? `text="${escapeXml(v.TextValue)}"` : "",
			]
				.filter(Boolean)
				.join(" ");
			return `<Row ${attrs} />`;
		})
		.join("");
	return `<XMLRoot>${rows}</XMLRoot>`;
};

export const saveStraightenerRecipe = async (
	productSizeId: number,
	name: string | null,
	crew: string | null,
	comments: string | null,
	values: StraightenerRecipeValueRow[],
	setAsCurrent = false,
): Promise<number> => {
	const api = new MESApiService();
	const params: spParameter[] = [
		{ name: "ProductSizeId", value: productSizeId },
		{ name: "Name", value: name },
		{ name: "Crew", value: crew },
		{ name: "Comments", value: comments },
		{ name: "Values", value: buildValuesXml(values) },
		{ name: "SetAsCurrent", value: setAsCurrent },
	];
	const resp = await api.callV2("[MILL].[SaveStraightenerRecipe]", params);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", 0) as number;
	}
	throw new Error(resp.message || "Error saving straightener recipe");
};

export const updateStraightenerRecipe = async (
	recipeId: number,
	name: string | null,
	crew: string | null,
	comments: string | null,
	values: StraightenerRecipeValueRow[],
): Promise<number> => {
	const api = new MESApiService();
	const params: spParameter[] = [
		{ name: "RecipeId", value: recipeId },
		{ name: "Name", value: name },
		{ name: "Crew", value: crew },
		{ name: "Comments", value: comments },
		{ name: "Values", value: buildValuesXml(values) },
	];
	const resp = await api.callV2("[MILL].[UpdateStraightenerRecipe]", params);
	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].Id", 0) as number;
	}
	throw new Error(resp.message || "Error updating straightener recipe");
};

// Latest live tag values (already scaled by the SP) shaped as recipe values, so
// a new recipe can be prefilled with the current line setup as a starting point.
export const getCurrentLineValues = async (): Promise<
	StraightenerRecipeValueRow[]
> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[GetStraightenerTrendDisplayDataWeb]", [
		{ name: "RecipeId", value: null },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching current line values");
	}
	const rows = get(resp, "data.tables[0].rows", []) as Array<{
		ParameterCode: string;
		RollNumber: number | null;
		Current: number | null;
	}>;
	return rows.map((r) => ({
		ParameterCode: r.ParameterCode,
		RollNumber: r.RollNumber ?? 0,
		Value: r.Current,
		MinValue: null,
		MaxValue: null,
		TextValue: null,
	}));
};

export const setStraightenerRecipeCurrent = async (
	recipeId: number,
): Promise<void> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[SetStraightenerRecipeCurrent]", [
		{ name: "RecipeId", value: recipeId },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error setting default recipe");
	}
};

export const setStraightenerRecipePlcCurrent = async (
	recipeId: number,
): Promise<void> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[SetStraightenerRecipePlcCurrent]", [
		{ name: "RecipeId", value: recipeId },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error setting PLC current recipe");
	}
};

export const setStraightenerRecipeActive = async (
	recipeId: number,
	active: boolean,
	// When deactivating the current recipe, the successor to promote as current
	// (null = leave the product without a current recipe).
	newCurrentRecipeId: number | null = null,
): Promise<void> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[SetStraightenerRecipeActive]", [
		{ name: "RecipeId", value: recipeId },
		{ name: "Active", value: active },
		{ name: "NewCurrentRecipeId", value: newCurrentRecipeId },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error updating recipe active state");
	}
};

export const removeStraightenerRecipe = async (id: number): Promise<void> => {
	const api = new MESApiService();
	const resp = await api.callV2("[MILL].[RemoveStraightenerRecipe]", [
		{ name: "Id", value: id },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error removing straightener recipe");
	}
};
